import type React from "react";
import { lazy, Suspense } from "react";
import { useTranslation } from "react-i18next";
import { Route } from "react-router-dom";
import { IS_SELF_HOSTED } from "@/constants/constants";
import type { SubscriptionType } from "@/interfaces/user";
import { LocalizedNavigate } from "@/router/localized-navigate";
import type { FliptFlagType } from "@/types";
import { API_KEYS_ROUTE } from "../../constants/routes";

const NotFoundPage = lazy(() => import("../NotFoundPage"));

type CatchAllNotFoundProps = {
	moduleKey: "nav.dashboard" | "nav.events" | "nav.devices";
	url: string;
};

const CatchAllNotFound = ({ moduleKey, url }: CatchAllNotFoundProps) => {
	const { t } = useTranslation("layout");
	return (
		<Suspense fallback={null}>
			<NotFoundPage module={t(moduleKey)} url={url} />
		</Suspense>
	);
};

import {
	deviceFingerprintAdminRoutes,
	deviceFingerprintOnlyRoutes,
	fullSubscriptionAdminRoutes,
	fullSubscriptionRouteGroups,
	legacySettingsRedirectRoutes,
} from "./main.routes";
import type { RouteConfig } from "./main.types";

/**
 * Checks if a route should be rendered based on its configuration
 */
export const shouldRenderRoute = (
	route: RouteConfig,
	hasAdminAccess: boolean,
	flagList?: FliptFlagType,
	subscriptions?: SubscriptionType[],
): boolean => {
	if (route.hiddenSelfHosted && IS_SELF_HOSTED) {
		return false;
	}

	// Check admin requirement
	if (route.requiresAdmin && !hasAdminAccess) {
		return false;
	}

	// Check feature flag requirement
	if (route.requiredFlag && !flagList?.[route.requiredFlag]) {
		return false;
	}

	// Check subscription exclusions
	if (route.excludeSubscriptions && subscriptions) {
		const hasExcludedSubscription = route.excludeSubscriptions.some((sub) =>
			subscriptions.includes(sub),
		);
		if (hasExcludedSubscription) {
			return false;
		}
	}

	return true;
};

/**
 * Builds route elements from route configuration
 */
export const buildRouteElements = (
	routes: RouteConfig[],
	hasAdminAccess: boolean,
	flagList?: FliptFlagType,
	subscriptions?: SubscriptionType[],
): React.ReactElement[] => {
	const elements: React.ReactElement[] = [];

	routes.forEach((route) => {
		if (shouldRenderRoute(route, hasAdminAccess, flagList, subscriptions)) {
			const path = typeof route.path === "function" ? route.path() : route.path;
			elements.push(<Route key={path} path={path} element={route.element} />);
		}
	});

	return elements;
};

/**
 * Generates all routes for device fingerprint only subscription
 */
export const generateDeviceFingerprintRoutes = (
	hasAdminAccess: boolean,
	isLoading: boolean,
): React.ReactElement[] => {
	const routes: React.ReactElement[] = [];

	// Add redirect route
	routes.push(
		<Route
			key="redirect"
			path="/"
			element={<LocalizedNavigate to={API_KEYS_ROUTE} replace />}
		/>,
	);

	// Add regular routes
	routes.push(...buildRouteElements(deviceFingerprintOnlyRoutes, false));

	// Legacy redirect routes (e.g. /api-keys → /settings/api-keys)
	routes.push(...buildRouteElements(legacySettingsRedirectRoutes, false));

	// Add admin routes if applicable
	if (hasAdminAccess) {
		routes.push(...buildRouteElements(deviceFingerprintAdminRoutes, true));
	}

	// Add catch-all route when not loading
	if (!isLoading) {
		routes.push(
			<Route
				key="catch-all"
				path="*"
				element={
					<CatchAllNotFound moduleKey="nav.devices" url={API_KEYS_ROUTE} />
				}
			/>,
		);
	}

	return routes;
};

/**
 * Generates all routes for full subscription users
 */
export const generateFullSubscriptionRoutes = (
	hasAdminAccess: boolean,
	flagList: FliptFlagType | undefined,
	subscriptions: SubscriptionType[],
	defaultRoute: string,
	isLoading: boolean,
): React.ReactElement[] => {
	const routes: React.ReactElement[] = [];

	// Add redirect route
	routes.push(
		<Route
			key="redirect"
			path="/"
			element={<LocalizedNavigate to={defaultRoute} replace />}
		/>,
	);

	// Add all route groups
	fullSubscriptionRouteGroups.forEach((group) => {
		routes.push(
			...buildRouteElements(group.routes, false, flagList, subscriptions),
		);
	});

	// Legacy redirect routes (e.g. /account-settings → /settings/account)
	routes.push(
		...buildRouteElements(
			legacySettingsRedirectRoutes,
			false,
			flagList,
			subscriptions,
		),
	);

	// Add admin routes if applicable
	if (hasAdminAccess) {
		routes.push(
			...buildRouteElements(
				fullSubscriptionAdminRoutes,
				true,
				flagList,
				subscriptions,
			),
		);
	}

	// Add catch-all route when not loading
	if (!isLoading) {
		routes.push(
			<Route
				key="catch-all"
				path="*"
				element={
					<CatchAllNotFound
						moduleKey={IS_SELF_HOSTED ? "nav.events" : "nav.dashboard"}
						url={defaultRoute}
					/>
				}
			/>,
		);
	}

	return routes;
};
